import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardHeader,
  Tooltip,
  IconButton,
  CardContent,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import API from "../../api/axiosConfig";
import { useUser } from "../../context/UserContext";
import { useToast } from "../../context/ToastContext";
import AccessDeniedPage from "../RedirectPages/AccessDeniedPage";
import AutocompleteUsers from "../../components/AutocompleteUsers";
import { DeleteConfirmationDialog } from "../../components/DeleteConfirmationDialog";

export default function ReimbursementDetailPage() {
  const showToast = useToast();
  const { isAdmin, userEmail } = useUser();
  const { id } = useParams();
  const navigate = useNavigate();

  // --- State Management ---
  const [reimbursement, setReimbursement] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);

  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  const fetchInfo = useCallback(async () => {
    try {
      const response = await API.post("/api/reimbursements/get", id, {
        headers: { "Content-Type": "application/json" },
      });
      const rData = response.data;
      setReimbursement(rData);
      setEditData(rData);
    } catch (err) {
      if (err.response?.status === 400 || err.response?.status === 403) {
        showToast("You are not permitted to access this page.", "error");
        navigate("/reimbursements");
      } else if (err.response?.status === 404) {
        showToast("No reimbursement found.", "error");
        navigate("/reimbursements");
      } else if (err.response?.status !== 401) {
        showToast("Error loading reimbursement information.", "error");
      }
    }
  }, [id, navigate, showToast]);

  const fetchImage = useCallback(async () => {
    try {
      const response = await API.get(`/api/reimbursements/image/${id}`, {
        responseType: "blob",
      });
      const objectUrl = URL.createObjectURL(response.data);
      setImageSrc(objectUrl);
    } catch (error) {
      const errorData = error.response?.data;
      if (
        error.response?.status === 404 &&
        errorData instanceof Blob &&
        errorData.type.startsWith("image/")
      ) {
        const fallbackUrl = URL.createObjectURL(errorData);
        setImageSrc(fallbackUrl);
      } else if (error.response?.status !== 404) {
        showToast("Error fetching receipt image.", "error");
      }
    }
  }, [id, showToast]);

  useEffect(() => {
    const initialFetch = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchInfo(), fetchImage()]);
      } catch (err) {
        showToast("Failed to load reimbursement data.", "error");
      } finally {
        setLoading(false);
      }
    };
    initialFetch();
  }, [fetchInfo, fetchImage, showToast]);

  useEffect(() => {
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  // Opens the dialog and stores the desired status ("APPROVED", "DENIED", etc.)
  const handleOpenStatusDialog = (status) => {
    if (!isAdmin) return;
    setTargetStatus(status);
    setStatusDialogOpen(true);
  };

  const handleSaveReimbursement = async () => {
    if (!editData?.paidToEmail) return;
    setSubmitting(true);
    try {
      const response = await API.post("/api/reimbursements/changeRecipient", {
        paidToEmail: editData.paidToEmail,
        parentEmail: reimbursement.parentEmail,
        reimbursementId: Number(id),
      });

      const updated = {
        paidToName: response.data.name,
        paidToEmail: response.data.email,
      };

      setReimbursement((prev) => ({ ...prev, ...updated }));
      setEditData((prev) => ({ ...prev, ...updated }));
      setIsEditing(false);
      showToast("Payee updated successfully!", "success");
    } catch (error) {
      showToast(
        error.response?.data?.message || "Failed to save payee.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await API.post(`/api/reimbursements/delete?id=${reimbursement.id}`, {});
      navigate("/reimbursements");
    } catch (err) {
      if (err.response?.status === 404) {
        navigate("/reimbursements");
      } else {
        showToast("Error deleting reimbursement.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmStatusUpdate = async () => {
    if (!targetStatus) return;

    setSubmitting(true);
    try {
      const params = new FormData();
      params.append("id", id);
      params.append("status", targetStatus);

      await API.post("/api/reimbursements/update", params);
      showToast(
        `Reimbursement marked as ${targetStatus.toLowerCase()}!`,
        "success",
      );
      setStatusDialogOpen(false);
      setTargetStatus(null);
      fetchInfo();
    } catch (err) {
      showToast("Error updating reimbursement status.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading reimbursement details...
        </Typography>
      </Box>
    );
  }

  if (
    !reimbursement ||
    (!isAdmin &&
      reimbursement.parentEmail !== userEmail &&
      reimbursement.paidToEmail !== userEmail)
  ) {
    return <AccessDeniedPage />;
  }

  const canEditPayee =
    reimbursement.status === "PENDING" &&
    (isAdmin || reimbursement.parentEmail === userEmail);
  const canDelete = isAdmin || reimbursement.parentEmail === userEmail;

  const getStatusChip = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <Chip
            label="Approved"
            color="success"
            size="small"
            icon={<CheckCircleOutlineIcon />}
          />
        );
      case "DENIED":
      case "REJECTED":
        return (
          <Chip
            label="Denied"
            color="error"
            size="small"
            icon={<CancelOutlinedIcon />}
          />
        );
      case "PENDING":
      default:
        return (
          <Chip
            label="Pending"
            color="warning"
            size="small"
            variant="outlined"
            icon={<HourglassEmptyIcon />}
          />
        );
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 4 } }}>
      {/* --- TOP ACTION HEADER --- */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              flexWrap="wrap"
            >
              <Typography variant="h4" fontWeight="bold">
                Reimbursement #{id}
              </Typography>
              {isAdmin && (
                <Chip
                  label="Admin View"
                  size="small"
                  color="secondary"
                  sx={{ fontWeight: "bold" }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Submitted by <strong>{reimbursement.parentName}</strong> (
              {reimbursement.parentEmail})
            </Typography>
          </Box>

          {/* Action Button Controls */}
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
          >
            {canDelete && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                disabled={submitting}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* --- 2-COLUMN DASHBOARD GRID --- */}
      <Grid container spacing={3}>
        {/* ================= LEFT COLUMN: SUMMARY & DETAILS ================= */}
        <Grid item xs={12} md={7}>
          <Card elevation={2} sx={{ borderRadius: 2, height: "100%" }}>
            <CardHeader
              avatar={<ReceiptLongOutlinedIcon color="primary" />}
              title="Claim Information"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <Divider />
            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: 3, p: 3 }}
            >
              {/* Financial Metrics Row */}
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <AttachMoneyOutlinedIcon
                      color="primary"
                      sx={{ fontSize: 32 }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="bold"
                      >
                        TOTAL AMOUNT
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {formatter.format(reimbursement.amount)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      pl: { sm: 2 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="bold"
                    >
                      CLAIM STATUS
                    </Typography>

                    <Stack
                      direction="column"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.5, width: "100%" }}
                    >
                      {getStatusChip(reimbursement.status)}
                    </Stack>
                  </Box>
                </Grid>
              </Grid>

              <Divider />

              {/* Payee Section */}
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="bold"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  PAYEE
                </Typography>

                {isEditing ? (
                  <Stack
                    direction="column"
                    spacing={2}
                    alignItems="center"
                    sx={{ maxWidth: 420, mx: "auto", mt: 1.5 }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <AutocompleteUsers
                        size="small"
                        label="Select New Payee"
                        value={editData?.paidToEmail || ""}
                        valueKey="email"
                        returnFullObject={true}
                        fullWidth
                        onChange={(selectedUser) => {
                          setEditData((prev) => ({
                            ...prev,
                            paidToName: selectedUser?.name || "",
                            paidToEmail: selectedUser?.email || "",
                          }));
                        }}
                      />
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent="center"
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={submitting || !editData?.paidToEmail}
                        onClick={handleSaveReimbursement}
                        startIcon={
                          submitting ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        sx={{ minWidth: 100 }}
                      >
                        {submitting ? "Saving..." : "Save"}
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        disabled={submitting}
                        onClick={() => {
                          setIsEditing(false);
                          setEditData(reimbursement);
                        }}
                        startIcon={<CloseIcon />}
                        sx={{ minWidth: 100 }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack alignItems="center" spacing={0.25} sx={{ mt: 0.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Typography variant="h6" fontWeight="bold">
                        {reimbursement.paidToName || "No payee assigned"}
                      </Typography>

                      {canEditPayee && (
                        <Tooltip title="Change Payee" arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditData(reimbursement);
                              setIsEditing(true);
                            }}
                            sx={{
                              p: 0.5,
                              color: "text.secondary",
                              "&:hover": { color: "primary.main" },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Stack>
                )}
              </Box>

              <Divider />

              {/* Purpose / Reason Block */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="bold"
                >
                  PURPOSE / NOTES
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 1,
                    borderRadius: 1.5,
                    bgcolor: "action.hover",
                    whiteSpace: "pre-line",
                  }}
                >
                  <Typography variant="body2">
                    {reimbursement.reason ||
                      "No purpose or notes provided for this reimbursement."}
                  </Typography>
                </Paper>
              </Box>
            </CardContent>
            {isAdmin && reimbursement.status === "PENDING" ? (
              <Box
                sx={{
                  p: 2,
                  px: 3,
                  bgcolor: "action.hover",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                >
                  Update Status
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={submitting}
                    onClick={() => handleOpenStatusDialog("DENIED")}
                    startIcon={<CancelOutlinedIcon />}
                  >
                    Deny Claim
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={submitting}
                    onClick={() => handleOpenStatusDialog("APPROVED")}
                    startIcon={<CheckCircleOutlineIcon />}
                  >
                    Approve Claim
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  disabled={submitting}
                  onClick={() => handleOpenStatusDialog("PENDING")}
                  startIcon={<RestartAltIcon />}
                >
                  Revert to Pending
                </Button>
              </Box>
            )}
          </Card>
        </Grid>

        {/* ================= RIGHT COLUMN: RECEIPT ATTACHMENT ================= */}
        <Grid item xs={12} md={5}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardHeader
              title="Receipt Attachment"
              titleTypographyProps={{ variant: "h6", fontWeight: "bold" }}
            />
            <Divider />
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
              }}
            >
              {imageSrc ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt="Receipt"
                  sx={{
                    width: "100%",
                    maxHeight: 520,
                    objectFit: "contain",
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 1,
                  }}
                />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 4,
                    width: "100%",
                    textAlign: "center",
                    bgcolor: "action.hover",
                    borderStyle: "dashed",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No receipt image is associated with this claim.
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        loading={submitting}
        objectToDelete="reimbursement"
      />
      <Dialog
        open={statusDialogOpen}
        onClose={() => !submitting && setStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontWeight="bold">Confirm Status Change</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark this reimbursement as{" "}
            <strong>{targetStatus ? targetStatus.toLowerCase() : ""}</strong>?
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setStatusDialogOpen(false)}
            disabled={submitting}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusUpdate}
            variant="contained"
            disabled={submitting}
            color={
              targetStatus === "APPROVED"
                ? "success"
                : targetStatus === "DENIED"
                  ? "error"
                  : "warning"
            }
            startIcon={
              submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {submitting ? "Updating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
